import { h } from 'preact';
import clsx from 'clsx';
import { useState, useEffect } from 'preact/hooks';
import { useThreekit } from '../../threekit-context';
import { useModal } from '../../modal-context';
import ChangeConfiguration from '../../threekit-components/change-configuration';
import Image from '../../../utils/image';
import ThreekitIcons from '../threekit-icons';
import { Swiper, SwiperSlide } from '../swiper';

function InspoModal() {
  const {
    configuration,
    inspo,
    productTag,
    darkMode,
    getBackgroundColorFromSelectedVariant,
  } = useThreekit();
  const { modalName, closeModal } = useModal();
  const [previousConfig, setPreviousConfig] = useState(null);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [selectedConfigIndex, setSelectedConfigIndex] = useState(null);
  const insporationObjects = inspo.filter(
    (obj) => obj.customProductTag === productTag,
  );
  const variantBackgroundColor = getBackgroundColorFromSelectedVariant();

  useEffect(() => {
    if (modalName === 'Inspo') {
      setPreviousConfig(configuration);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalName]);

  return (
    <div className="laptop:pt-5 laptop:pl-5 pl-2.5 py-5">
      <div className="flex items-center justify-between mx-2 mb-5 laptop:mx-5">
        <h5
          className={clsx(
            'uppercase font-b text-[1rem] laptop:text-[1.375rem]',
            darkMode ? 'text-white' : 'text-black',
          )}
        >
          Inspo
        </h5>
        <div className="flex gap-[13px] [laptop:gap-[25px] justify-between items-center">
          <p
            className={clsx(
              'font-r-10 laptop:font-r-12',
              darkMode ? 'text-white' : 'text-black',
            )}
          >
            Applying a style will override previous selections
          </p>
          <button
            className={clsx(
              'py-2.5 px-5 bg-bg-2 font-m-12 uppercase text-white hidden laptop:block',
              {
                'text-white':
                  selectedConfigIndex === null
                  || variantBackgroundColor?.isDark,
                'text-black': !(
                  selectedConfigIndex === null || variantBackgroundColor?.isDark
                ),
              },
            )}
            onClick={() => closeModal()}
            disabled={selectedConfig === null}
            style={{
              backgroundColor:
                selectedConfigIndex !== null
                  ? variantBackgroundColor.hex
                  : 'black',
            }}
          >
            Apply this Style
          </button>
          <ChangeConfiguration
            element="button"
            configuration={previousConfig}
            additionalOnClick={() => closeModal()}
          >
            <ThreekitIcons type="exitIcon" />
          </ChangeConfiguration>
        </div>
      </div>
      <button
        className={clsx(
          'absolute bottom-0 left-0 w-full py-2 text-white btn-secondary laptop:hidden',
          {
            'text-white':
              selectedConfigIndex === null || variantBackgroundColor?.isDark,
            'text-black': !(
              selectedConfigIndex === null || variantBackgroundColor?.isDark
            ),
          },
        )}
        onClick={() => closeModal()}
        disabled={selectedConfig === null}
        style={{
          backgroundColor:
            selectedConfigIndex !== null ? variantBackgroundColor.hex : 'black',
        }}
      >
        Apply this Style
      </button>
      <Swiper
        slidesPerView={2}
        spaceBetween={10}
        slidesOffsetAfter={320}
        breakpoints={{
          1024: {
            slidesPerView: 3,
            spaceBetween: 28,
          },
        }}
        scrollable
      >
        {insporationObjects.map((object, i) => (
          <SwiperSlide
            key={`${object.threekitId}-inspo-modal`}
            className={clsx(
              'min-w-[260px] max-w-[386px] py-[19px] px-[16px] border bg-bg-2 laptop:bg-transparent bg-opacity-20 !h-auto',
              {
                'border-transparent': selectedConfigIndex !== i,
                'border-white':
                  selectedConfig === object.threekitId
                  && selectedConfigIndex === i
                  && darkMode,
                'border-black':
                  selectedConfig === object.threekitId
                  && selectedConfigIndex === i
                  && !darkMode,
              },
            )}
          >
            <ChangeConfiguration
              element="button"
              className="flex flex-col justify-between w-full h-full"
              configId={selectedConfigIndex !== i && object.threekitId}
              configuration={selectedConfigIndex === i && previousConfig}
              additionalOnClick={() => {
                if (selectedConfigIndex !== i) {
                  setSelectedConfig(object.threekitId);
                  setSelectedConfigIndex(i);
                  return;
                }
                setSelectedConfig(null);
                setSelectedConfigIndex(null);
              }}
            >
              <div className="hidden laptop:block absolute w-full h-full top-[22%] opacity-0 hover:opacity-70 transition duration-150 ease-out hover:ease-in">
                <span className="py-2.5 px-5 bg-bg-2 font-m-12 uppercase text-white border border-white">
                  Preview
                </span>
              </div>
              <Image
                className="mb-2 h-[50px] laptop:h-[94px] object-cover object-center"
                src={object.image?.src || object.image}
                width={360}
                height={81}
              />
              <div className="flex items-end justify-between w-full">
                <div className="flex items-baseline laptop:gap-5 laptop:mb-2 flex-col laptop:flex-row  min-w-[138px]">
                  <span
                    className={clsx(
                      'uppercase laptop:font-b-16 font-m-12 text-left',
                      darkMode ? 'text-white' : 'text-black',
                    )}
                  >
                    {object.name}
                  </span>
                  <span
                    className={clsx(
                      'font-r-14',
                      darkMode ? 'text-white' : 'text-black',
                    )}
                  >
                    {object.price}
                  </span>
                </div>
                <span
                  className={clsx(
                    'w-auto px-[15px] py-[8px] border laptop:hidden btn btn-primary',
                    darkMode
                      ? 'text-white border-white hover:text-white hover:border-white'
                      : 'text-black border-black hover:text-black hover:border-black',
                  )}
                >
                  {selectedConfig === object.threekitId
                  && selectedConfigIndex === i
                    ? 'Remove'
                    : 'Apply'}
                </span>
              </div>
              <p
                className={clsx(
                  'hidden text-left font-r-12 laptop:block',
                  darkMode ? 'text-white' : 'text-black',
                )}
              >
                {object.blurb}
              </p>
            </ChangeConfiguration>
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
}

export default InspoModal;
